import { getApiHeaders, getApiUrl } from '../utils/apiConfig';
import { getAuthHeaders } from '../utils/userUtils';

const FRIENDLY_ERROR = '잠시 후 다시 시도해 주세요.';

const readDetail = (payload) => {
  const detail = payload?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  return '';
};

const request = async (user, path, { method = 'GET', body } = {}) => {
  const headers = user ? await getAuthHeaders(user) : getApiHeaders();
  const response = await fetch(getApiUrl(path), {
    method,
    headers: { ...headers, 'Content-Type': 'application/json' },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  let payload = null;
  try {
    payload = await response.json();
  } catch (_error) {
    payload = null;
  }
  if (!response.ok) {
    const error = new Error(readDetail(payload) || FRIENDLY_ERROR);
    error.status = response.status;
    throw error;
  }
  return payload;
};

export const getMyOrganization = (user) => request(user, '/api/org/me');

export const submitAdminRequest = (user, form) => request(user, '/api/org/admin-requests', {
  method: 'POST',
  body: form,
});

export const previewInvite = (token, user = null) => request(
  user,
  `/api/org/invite/${encodeURIComponent(token)}`,
);

export const joinOrganization = (user, token) => request(
  user,
  `/api/org/join/${encodeURIComponent(token)}`,
  { method: 'POST' },
);

export const getOrgDashboard = (user, orgId, { refresh = false } = {}) => request(
  user,
  `/api/org/${encodeURIComponent(orgId)}/dashboard${refresh ? '?refresh=true' : ''}`,
);

export const removeOrgMember = (user, orgId, memberUid) => request(
  user,
  `/api/org/${encodeURIComponent(orgId)}/members/${encodeURIComponent(memberUid)}/remove`,
  { method: 'POST' },
);

export const reclaimOrgSeat = (user, orgId, memberUid, { reason, memo = '' }) => request(
  user,
  `/api/org/${encodeURIComponent(orgId)}/members/${encodeURIComponent(memberUid)}/reclaim`,
  { method: 'POST', body: { reason, memo } },
);

export const rotateOrgInvite = (user, orgId) => request(
  user,
  `/api/org/${encodeURIComponent(orgId)}/invite/rotate`,
  { method: 'POST' },
);

export const inviteUrlFor = (token) => `${window.location.origin}/join/${token}`;

export const listOrgAdminRequests = (user, status = 'pending') => request(
  user,
  `/api/admin/organizations/requests?status=${encodeURIComponent(status)}`,
);

export const approveOrgAdminRequest = (user, requestId, body) => request(
  user,
  `/api/admin/organizations/requests/${encodeURIComponent(requestId)}/approve`,
  { method: 'POST', body },
);

export const rejectOrgAdminRequest = (user, requestId, reason) => request(
  user,
  `/api/admin/organizations/requests/${encodeURIComponent(requestId)}/reject`,
  { method: 'POST', body: { reason } },
);

export const listOrganizations = (user) => request(user, '/api/admin/organizations');

export const listOrgBatches = (user, { minSeats = 10 } = {}) => request(
  user,
  `/api/admin/organizations/batches?min_seats=${encodeURIComponent(minSeats)}`,
);

export const createOrganization = (user, body) => request(user, '/api/admin/organizations', { method: 'POST', body });

const reviewPath = (orgId, reviewId = '', tail = '') => (
  `/api/org/${encodeURIComponent(orgId)}/reviews${reviewId ? `/${encodeURIComponent(reviewId)}` : ''}${tail}`
);

const rawRequest = async (user, path, { method = 'GET', body } = {}) => {
  const headers = { ...(await getAuthHeaders(user)) };
  delete headers['Content-Type'];
  const response = await fetch(getApiUrl(path), { method, headers, body });
  if (!response.ok) {
    let payload = null;
    try {
      payload = await response.json();
    } catch (_error) {
      payload = null;
    }
    const error = new Error(readDetail(payload) || FRIENDLY_ERROR);
    error.status = response.status;
    throw error;
  }
  return response;
};

export const listSchoolReviews = (user, orgId) => request(user, reviewPath(orgId));

export const createSchoolReview = (user, orgId, body) => request(user, reviewPath(orgId), { method: 'POST', body });

export const getSchoolReview = (user, orgId, reviewId) => request(user, reviewPath(orgId, reviewId));

export const deleteSchoolReview = (user, orgId, reviewId) => request(user, reviewPath(orgId, reviewId), { method: 'DELETE' });

export const uploadSchoolReviewFile = async (user, orgId, reviewId, file) => {
  const form = new FormData();
  form.append('file', file);
  const response = await rawRequest(user, reviewPath(orgId, reviewId, '/files'), { method: 'POST', body: form });
  return response.json();
};

export const listSchoolReviewEntries = (user, orgId, reviewId, filters = {}) => {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') params.set(key, String(value));
  });
  const query = params.toString();
  return request(user, reviewPath(orgId, reviewId, `/entries${query ? `?${query}` : ''}`));
};

export const getSchoolReviewEntry = (user, orgId, reviewId, entryId) => request(
  user,
  reviewPath(orgId, reviewId, `/entries/${encodeURIComponent(entryId)}`),
);

export const markSchoolReviewEntries = (user, orgId, reviewId, body) => request(
  user,
  reviewPath(orgId, reviewId, '/marks'),
  { method: 'POST', body },
);

export const closeSchoolReviewRound = (user, orgId, reviewId, body) => request(
  user,
  reviewPath(orgId, reviewId, '/rounds/close'),
  { method: 'POST', body },
);

export const fixSchoolReviewPhrase = (user, orgId, reviewId, body) => request(
  user,
  reviewPath(orgId, reviewId, '/marks/by-phrase'),
  { method: 'POST', body },
);

export const undoSchoolReviewPhrase = (user, orgId, reviewId, batchId) => request(
  user,
  reviewPath(orgId, reviewId, '/marks/by-phrase/undo'),
  { method: 'POST', body: { batch_id: batchId } },
);

export const aiCheckSchoolReviewEntries = (user, orgId, reviewId, entryIds) => request(
  user,
  reviewPath(orgId, reviewId, '/ai-check'),
  { method: 'POST', body: { entry_ids: entryIds } },
);

export const exportSchoolReview = async (user, orgId, reviewId, states = ['fix', 'fixed']) => {
  const response = await rawRequest(
    user,
    reviewPath(orgId, reviewId, `/export?states=${encodeURIComponent(states.join(','))}`),
  );
  return response.blob();
};

export const downloadSchoolReviewReport = async (user, orgId, reviewId) => {
  const response = await rawRequest(user, reviewPath(orgId, reviewId, '/report'));
  return response.blob();
};

export const getSchoolReviewClasses = (user, orgId, reviewId) => request(user, reviewPath(orgId, reviewId, '/classes'));

export const saveSchoolReviewClasses = (user, orgId, reviewId, body) => request(
  user,
  reviewPath(orgId, reviewId, '/classes'),
  { method: 'PUT', body },
);

export const confirmSchoolReviewTeacher = (user, orgId, reviewId, teacherUid, done = true) => request(
  user,
  reviewPath(orgId, reviewId, `/teachers/${encodeURIComponent(teacherUid)}/confirm`),
  { method: 'POST', body: { done } },
);

export const notifySchoolReviewTeachers = (user, orgId, reviewId, uids = []) => request(
  user,
  reviewPath(orgId, reviewId, '/notify'),
  { method: 'POST', body: { uids } },
);

export const saveSchoolReviewReviewers = (user, orgId, reviewId, grades) => request(
  user,
  reviewPath(orgId, reviewId, '/reviewers'),
  { method: 'PUT', body: { grades } },
);

export const announceSchoolReview = (user, orgId, reviewId) => request(
  user,
  reviewPath(orgId, reviewId, '/announce'),
  { method: 'POST' },
);

export const getSchoolReviewGuide = (user, orgId) => request(user, `/api/org/${encodeURIComponent(orgId)}/review-guide`);

export const saveSchoolReviewGuide = (user, orgId, body) => request(
  user,
  `/api/org/${encodeURIComponent(orgId)}/review-guide`,
  { method: 'PUT', body },
);

const myReviewPath = (orgId, reviewId = '', tail = '') => (
  `/api/org/${encodeURIComponent(orgId)}/my-reviews${reviewId ? `/${encodeURIComponent(reviewId)}` : ''}${tail}`
);

export const listMyReviews = (user, orgId) => request(user, myReviewPath(orgId));

export const getMyReview = (user, orgId, reviewId) => request(user, myReviewPath(orgId, reviewId));

export const listMyReviewEntries = (user, orgId, reviewId, filters = {}) => {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') params.set(key, String(value));
  });
  const query = params.toString();
  return request(user, myReviewPath(orgId, reviewId, `/entries${query ? `?${query}` : ''}`));
};

export const getMyReviewEntry = (user, orgId, reviewId, entryId) => request(
  user,
  myReviewPath(orgId, reviewId, `/entries/${encodeURIComponent(entryId)}`),
);

export const draftMyReviewEntry = (user, orgId, reviewId, entryId, facts = '') => request(
  user,
  myReviewPath(orgId, reviewId, `/entries/${encodeURIComponent(entryId)}/draft`),
  { method: 'POST', body: { facts } },
);

export const getMyReviewScope = (user, orgId, reviewId) => request(user, myReviewPath(orgId, reviewId, '/scope'));

export const listMyScopeStudents = (user, orgId, reviewId, classKey) => request(
  user,
  myReviewPath(orgId, reviewId, `/scope/students?class_key=${encodeURIComponent(classKey)}`),
);

export const getMyScopeStudent = (user, orgId, reviewId, classKey, studentId) => request(
  user,
  myReviewPath(orgId, reviewId, `/scope/student?class_key=${encodeURIComponent(classKey)}&student=${encodeURIComponent(studentId)}`),
);

export const getMyScopeFixed = (user, orgId, reviewId) => request(
  user,
  myReviewPath(orgId, reviewId, '/scope/fixed'),
);

export const markMyScopeEntries = (user, orgId, reviewId, body) => request(
  user,
  myReviewPath(orgId, reviewId, '/scope/marks'),
  { method: 'POST', body },
);

export const uploadMyReviewFile = async (user, orgId, reviewId, file) => {
  const form = new FormData();
  form.append('file', file);
  const response = await rawRequest(user, myReviewPath(orgId, reviewId, '/files'), { method: 'POST', body: form });
  return response.json();
};

export const getSchoolReviewDemoAccess = (user) => request(user, '/api/admin/school-review-demo');

export const resetSchoolReviewDemo = (user) => request(user, '/api/admin/school-review-demo', { method: 'POST' });

export const removeSchoolReviewDemo = (user) => request(user, '/api/admin/school-review-demo', { method: 'DELETE' });
